/**
 * Validates the `place_order` tool arguments the model streams in, before the terminal
 * raises the consent gate and routes the order through the trading hub. Mirrors the strict
 * server schema in AgentToolDefinitions.PlaceOrderSchema and fails closed on anything odd.
 */

/** Max volume (lots) the agent may request in one order — mirrors VybeLlmOptions.MaxOrderVolume. */
export const AGENT_MAX_ORDER_VOLUME = 1.0;

export interface AgentPlaceOrderInput {
  action: "BUY" | "SELL";
  symbol: string;
  volume: number;
  stopLoss?: number;
  takeProfit?: number;
}

export type ValidatePlaceOrderResult =
  | { ok: true; order: AgentPlaceOrderInput }
  | { ok: false; error: string };

function asFiniteNumber(v: unknown): number | undefined {
  if (typeof v !== "number" || !Number.isFinite(v)) return undefined;
  return v;
}

export function validatePlaceOrderInput(
  input: unknown,
): ValidatePlaceOrderResult {
  const obj = input as Record<string, unknown> | undefined;
  if (!obj || typeof obj !== "object") {
    return { ok: false, error: "place_order requires an object argument." };
  }

  const rawAction =
    typeof obj.action === "string" ? obj.action.trim().toUpperCase() : "";
  if (rawAction !== "BUY" && rawAction !== "SELL") {
    return { ok: false, error: "place_order.action must be 'BUY' or 'SELL'." };
  }

  const symbol = typeof obj.symbol === "string" ? obj.symbol.trim() : "";
  if (!symbol) {
    return { ok: false, error: "place_order.symbol is required." };
  }

  const volume = asFiniteNumber(obj.volume);
  if (volume === undefined || volume <= 0) {
    return {
      ok: false,
      error: "place_order.volume must be a positive number of lots.",
    };
  }
  if (volume > AGENT_MAX_ORDER_VOLUME) {
    return {
      ok: false,
      error: `place_order.volume ${volume} exceeds the agent limit of ${AGENT_MAX_ORDER_VOLUME} lots.`,
    };
  }

  const stopLoss = asFiniteNumber(obj.stopLoss);
  const takeProfit = asFiniteNumber(obj.takeProfit);

  return {
    ok: true,
    order: {
      action: rawAction,
      symbol,
      volume,
      ...(stopLoss !== undefined ? { stopLoss } : {}),
      ...(takeProfit !== undefined ? { takeProfit } : {}),
    },
  };
}
